/**
 * ModeSegmented — the app-wide REAL / DEMO mode toggle.
 *
 * Rendered in three surfaces:
 *   1. AppShell topbar right cluster (`size="regular"`, hidden < lg)
 *   2. AppShell mobile strip below the topbar (`size="mobile"`, < md only)
 *   3. Game.jsx top strip (`size="compact"`, always visible)
 *
 * Both balances are formatted through the app-wide CurrencyContext so the
 * segmented control follows the topbar dropdown (USD → BDT → INR → PKR →
 * BTC → USDT → ETH). REAL renders `—` when the real balance is zero.
 */
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCurrency } from "../../context/CurrencyContext";

function fmtReal(fmt, n) {
  const v = Number(n || 0);
  if (v <= 0) return "—";
  return fmt(v);
}
function fmtDemo(fmt, n) {
  return fmt(Number(n || 0));
}

const SIZES = {
  regular: {
    container: "h-10 rounded-full text-[13px]",
    segment: "px-4 gap-1.5",
    label: "text-[10px] tracking-[0.28em]",
    number: "text-[13px]",
    minWidth: "min-w-[260px]",
  },
  mobile: {
    container: "h-12 rounded-full text-sm w-full",
    segment: "px-4 gap-2",
    label: "text-[11px] tracking-[0.3em]",
    number: "text-[14px]",
    minWidth: "",
  },
  compact: {
    container: "h-9 rounded-full text-xs",
    segment: "px-3 gap-1",
    label: "text-[9px] tracking-[0.28em]",
    number: "text-[12px]",
    minWidth: "min-w-[176px]",
  },
};

export default function ModeSegmented({
  mode,
  setMode,
  realBalance = 0,
  demoBalance = 0,
  disabled = false,
  size = "regular",
  className = "",
}) {
  const nav = useNavigate();
  const { format } = useCurrency();
  const s = SIZES[size] || SIZES.regular;
  const zeroReal = Number(realBalance || 0) <= 0;

  const handleReal = () => {
    if (disabled) return;
    if (zeroReal) {
      toast("Deposit to play with real balance", {
        description: "Redirecting to the deposit page…",
        duration: 1600,
      });
      setTimeout(() => nav("/wallet/deposit"), 400);
      return;
    }
    if (mode !== "real") setMode("real");
  };
  const handleDemo = () => {
    if (disabled) return;
    if (mode !== "demo") setMode("demo");
  };

  const activeCls =
    "bg-gradient-to-r from-pink to-purple text-white shadow-glow-pink";
  const inactiveCls =
    "bg-highest/60 text-ink-2 hover:text-ink hover:bg-highest";

  return (
    <div
      className={
        "inline-flex items-stretch border border-line overflow-hidden " +
        s.container + " " + s.minWidth + " " + className
      }
      role="group"
      aria-label="Wallet mode"
      data-testid="mode-toggle-container"
    >
      <button
        type="button"
        onClick={handleReal}
        disabled={disabled}
        aria-pressed={mode === "real"}
        className={
          "flex-1 flex items-center justify-center transition-all active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-pink " +
          s.segment + " " +
          (mode === "real" ? activeCls : inactiveCls) +
          (disabled ? " opacity-60 cursor-not-allowed" : " cursor-pointer") +
          (zeroReal && mode !== "real" ? " opacity-70" : "")
        }
        data-testid="mode-toggle-real"
        data-legacy-testids="topbar-mode-real mode-real-btn"
      >
        <span className={"font-semibold uppercase " + s.label}>REAL</span>
        <span
          className={
            "font-mono font-semibold " + s.number + " " +
            (mode === "real" ? "text-gold" : zeroReal ? "text-ink-3" : "text-gold/85")
          }
        >
          {fmtReal(format, realBalance)}
        </span>
      </button>
      <button
        type="button"
        onClick={handleDemo}
        disabled={disabled}
        aria-pressed={mode === "demo"}
        className={
          "flex-1 flex items-center justify-center transition-all active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-pink " +
          s.segment + " " +
          (mode === "demo" ? activeCls : inactiveCls) +
          (disabled ? " opacity-60 cursor-not-allowed" : " cursor-pointer")
        }
        data-testid="mode-toggle-demo"
        data-legacy-testids="topbar-mode-demo mode-demo-btn"
      >
        <span className={"font-semibold uppercase " + s.label}>DEMO</span>
        <span
          className={
            "font-mono font-semibold " + s.number + " " +
            (mode === "demo" ? "text-white" : "text-ink-2")
          }
        >
          {fmtDemo(format, demoBalance)}
        </span>
      </button>
    </div>
  );
}

// Legacy testid aliases (rendered by AppShell / Game for backwards compat with
// pre-existing e2e tests that assert on `topbar-mode-pill` / `mode-pill`).
export function LegacyModeAliases() {
  return (
    <>
      <span className="hidden" data-testid="topbar-mode-pill" aria-hidden="true" />
      <span className="hidden" data-testid="mode-pill" aria-hidden="true" />
    </>
  );
}
